import { useCallback, useEffect, useState } from 'react';
import { translations, TranslationKey, Language } from './translations';
import { getLocaleFromPathname } from './localeRouting';

const DEFAULT_LANGUAGE: Language = 'en';
const LANGUAGE_EVENT = 'app-language-changed';

type LanguageChangedEvent = CustomEvent<{ language: Language }>;

export function getCurrentLanguage(): Language {
  if (typeof window !== 'undefined') {
    const fromUrl = getLocaleFromPathname(window.location.pathname);
    if (fromUrl) {
      return fromUrl;
    }

    const saved = localStorage.getItem('app_language');
    if (saved && saved in translations) {
      return saved as Language;
    }
    const browserLang = (navigator.language || '').toLowerCase();
    if (browserLang.startsWith('pl') && 'pl' in translations) {
      return 'pl';
    }
  }
  return DEFAULT_LANGUAGE;
}

export function setAppLanguage(language: Language) {
  if (!(language in translations)) return;
  localStorage.setItem('app_language', language);
  window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT, { detail: { language } }));
}

export function useTranslation() {
  const [language, setLanguage] = useState<Language>(() => getCurrentLanguage());

  useEffect(() => {
    const syncLanguage = (event: Event) => {
      const nextLanguage = (event as LanguageChangedEvent).detail?.language;
      setLanguage(nextLanguage ?? getCurrentLanguage());
    };
    window.addEventListener(LANGUAGE_EVENT, syncLanguage);
    return () => window.removeEventListener(LANGUAGE_EVENT, syncLanguage);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string>): string => {
      let text = translations[language]?.[key] || translations[DEFAULT_LANGUAGE][key] || key;

      if (params) {
        Object.keys(params).forEach((paramKey) => {
          text = text.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), params[paramKey]);
        });
      }

      return text;
    },
    [language]
  );

  return { t, language, setLanguage: setAppLanguage };
}

export function translate(key: TranslationKey, language?: Language): string {
  const lang = language || getCurrentLanguage();
  return translations[lang]?.[key] || translations[DEFAULT_LANGUAGE][key] || key;
}

export function getDeviceTranslationKey(deviceValue: string): TranslationKey {
  if (deviceValue === 'iPad / Tablet') return 'device.ipad';
  if (deviceValue === 'Laptop HP with greenscreen') return 'device.laptop_hp_with_greenscreen';
  const key = `device.${deviceValue.toLowerCase()}` as TranslationKey;
  return key;
}

export function getSceneTypeTranslationKey(sceneValue: string): TranslationKey {
  if (sceneValue === '') return 'sceneType.auto';
  return `sceneType.${sceneValue}` as TranslationKey;
}

export function getAspectRatioTranslationKey(ratioValue: string): TranslationKey {
  return `aspectRatio.${ratioValue}` as TranslationKey;
}

export function getPeopleTranslationKey(peopleValue: string): TranslationKey {
  if (peopleValue === '') return 'people.auto';
  return `people.${peopleValue}` as TranslationKey;
}

export function getGenderTranslationKey(genderValue: string): TranslationKey {
  if (genderValue === '') return 'gender.diverse';
  if (genderValue === 'non-binary') return 'gender.non_binary';
  return `gender.${genderValue}` as TranslationKey;
}

export function getEthnicityTranslationKey(ethnicityValue: string): TranslationKey {
  if (ethnicityValue === '') return 'ethnicity.diverse';
  return `ethnicity.${ethnicityValue.replace('-', '_')}` as TranslationKey;
}

export function getAgeTranslationKey(ageValue: string): TranslationKey {
  if (ageValue === '') return 'age.all';
  return `age.${ageValue.replace('-', '_')}` as TranslationKey;
}

export function getInteractionTranslationKey(interactionValue: string): TranslationKey {
  if (interactionValue === '') return 'people.interaction.none';
  return `people.interaction.${interactionValue}` as TranslationKey;
}

export function getShotPlanTranslationKey(shotPlanValue: string): TranslationKey {
  if (shotPlanValue === '') return 'aspectRatio.auto';

  if (shotPlanValue === 'extreme_close_up') return 'cameraSettings.extremeCloseUp';
  if (shotPlanValue === 'close_up') return 'cameraSettings.closeUp';
  if (shotPlanValue === 'medium_close_up') return 'cameraSettings.mediumCloseUp';
  if (shotPlanValue === 'medium') return 'cameraSettings.medium';
  if (shotPlanValue === 'medium_wide') return 'cameraSettings.mediumWide';
  if (shotPlanValue === 'wide') return 'cameraSettings.wide';
  if (shotPlanValue === 'very_wide') return 'cameraSettings.veryWide';
  if (shotPlanValue === 'extreme_wide') return 'cameraSettings.extremeWide';
  if (shotPlanValue === 'american') return 'cameraSettings.american';
  if (shotPlanValue === 'two_shot') return 'cameraSettings.twoShot';
  if (shotPlanValue === 'over_shoulder') return 'cameraSettings.overShoulder';
  if (shotPlanValue === 'point_of_view') return 'cameraSettings.pointOfView';

  return `cameraSettings.${shotPlanValue}` as TranslationKey;
}

export function getLocationTranslationKey(locationValue: string): TranslationKey {
  if (locationValue === '' || locationValue === 'none') return 'location.none';
  return `location.${locationValue}` as TranslationKey;
}

export function getSceneTypeDescriptionKey(sceneValue: string): TranslationKey {
  if (sceneValue === '') return 'sceneType.auto.desc';
  return `sceneType.${sceneValue}.desc` as TranslationKey;
}

export function getIllustrationStyleKey(styleValue: string): TranslationKey {
  if (styleValue === 'action') return 'illustrations.style.auto';
  return `illustrations.style.${styleValue}` as TranslationKey;
}

export function getPeopleCountTranslationKey(countValue: string): TranslationKey {
  if (countValue === 'auto') return 'people.auto';
  if (countValue === '5+') return 'people.count.5_plus';
  return `people.count.${countValue.replace('-', '_')}` as TranslationKey;
}

export function formatDate(timestamp: string | number): string {
  const date = new Date(timestamp);
  const lang = getCurrentLanguage();
  const locale = lang === 'pl' ? 'pl-PL' : 'en-GB';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
}
